import { CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import RealtimeKitClient from '@cloudflare/realtimekit';
import { RealtimeKitComponentsModule } from '@cloudflare/realtimekit-angular-ui';
import { defineCustomElements } from '@cloudflare/realtimekit-ui/loader';

type AdminMeetingState = 'connecting' | 'joined' | 'error';

@Component({
  selector: 'app-admin-meeting',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [RealtimeKitComponentsModule],
  template: `
    <div class="h-screen w-screen bg-neutral-900 flex flex-col">
      <!-- Header bar -->
      <div class="flex items-center justify-between px-6 py-3 bg-neutral-800">
        <div class="flex items-center gap-3">
          <span class="text-white font-medium" i18n>Admin Observer</span>
          <span class="text-neutral-400 text-sm">Session #{{ sessionId }}</span>
          <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-green-900/50 text-green-400 text-xs">
            <span class="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
            <span i18n>Hidden</span>
          </span>
        </div>
        <button (click)="leaveSession()" class="px-4 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700" i18n>
          Leave
        </button>
      </div>
      <!-- Meeting area -->
      <div class="flex-1">
        @switch (viewState()) {
          @case ('connecting') {
            <div class="flex items-center justify-center h-full">
              <p class="text-neutral-400" i18n>Connecting to session...</p>
            </div>
          }
          @case ('error') {
            <div class="flex items-center justify-center h-full text-center">
              <div>
                <p class="text-red-400 text-lg mb-2">{{ errorMessage() }}</p>
                <button (click)="leaveSession()" class="text-neutral-400 underline text-sm" i18n>Close</button>
              </div>
            </div>
          }
          @case ('joined') {
            @if (meeting()) {
              <rtk-meeting [meeting]="meeting()!" style="height:100%;width:100%;"></rtk-meeting>
            }
          }
        }
      </div>
    </div>
  `,
})
export class AdminMeetingComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);

  sessionId = 0;
  meeting = signal<RealtimeKitClient | null>(null);
  viewState = signal<AdminMeetingState>('connecting');
  errorMessage = signal('');

  async ngOnInit(): Promise<void> {
    // Register Stencil web components for RTK pre-built UI
    defineCustomElements(window);

    this.sessionId = Number(this.route.snapshot.paramMap.get('sessionId'));

    const token = sessionStorage.getItem('admin_rtk_token');
    const room = sessionStorage.getItem('admin_rtk_room');

    // Clean up sessionStorage after reading
    sessionStorage.removeItem('admin_rtk_token');
    sessionStorage.removeItem('admin_rtk_room');

    if (!token) {
      this.viewState.set('error');
      this.errorMessage.set('No auth token found. Please join from the session detail page.');
      return;
    }

    try {
      const client = await RealtimeKitClient.init({ authToken: token });
      this.meeting.set(client);

      // Disable audio/video so admin is hidden observer
      await client.self.disableAudio();
      await client.self.disableVideo();

      this.viewState.set('joined');
    } catch (err) {
      this.viewState.set('error');
      this.errorMessage.set(err instanceof Error ? err.message : 'Failed to connect to meeting');
    }
  }

  async leaveSession(): Promise<void> {
    const client = this.meeting();
    if (client) {
      try {
        await client.leaveRoom();
      } catch {
        // Ignore errors during cleanup
      }
      this.meeting.set(null);
    }
    window.close();
  }

  async ngOnDestroy(): Promise<void> {
    const client = this.meeting();
    if (client) {
      try {
        await client.leaveRoom();
      } catch {
        // Ignore errors during cleanup
      }
    }
  }
}
